import { useState } from 'react';
import {
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem as MuiMenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { router } from '@inertiajs/react';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import {
  commonMenuItems,
  hrItems,
} from '@/components/layout/header/constants/user-menu-items';
import type { MenuItem } from '@/components/layout/header/interfaces';
import { filterMenu } from '@/components/layout/header/utils';
import { UserAvatar } from '@/components/shared/user-avatar';
import { removeAuthToken } from '@/utils/auth-token';
import { useProfileShow } from '@/data/account/profile';
import { useUser } from '@/data/user';
import { useBootstrapData } from '@/data/user/bootstrap-data';
import { useNavigate } from '@/hooks/use-navigation';
import { lineClamp } from '@/utils/line-clamp';
import { session_path } from '@/utils/routes';

export const UserMenu = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useUser();
  const { bootstrap } = useBootstrapData();
  const { data } = useProfileShow();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleLogout = () => {
    Sentry.setUser(null);
    queryClient.clear();
    queryClient.resetQueries();
    removeAuthToken();
    router.delete(session_path());
  };

  const handleItemClick = (item: MenuItem) => {
    if (item.onClick) item.onClick();
    setAnchorEl(null);
    if (item.path) {
      const path =
        typeof item.path === 'function'
          ? item.path({ bootstrap, user })
          : item.path;

      if (path) navigate(path);
    }
  };

  const main = filterMenu({ user, bootstrap, items: commonMenuItems });
  const rh = filterMenu({ user, bootstrap, items: hrItems });

  return (
    <div>
      <IconButton
        onClick={(event) => setAnchorEl(event.currentTarget)}
        aria-label="Menu do usuário"
      >
        <UserAvatar
          name={data?.name ?? ''}
          variant="navbar"
          imageSource={data?.avatar}
        />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        sx={{ width: 280 }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Stack direction="row" spacing={2} alignItems="center" padding={2}>
          <UserAvatar
            name={data?.name ?? ''}
            variant="navbar"
            imageSource={data?.avatar}
          />
          <Box flex={1} sx={{ overflow: 'hidden' }}>
            <Typography sx={lineClamp(2)} variant="h3" mb={1}>
              {data?.name ?? ''}
            </Typography>
            <Typography variant="body2" sx={lineClamp(1)}>
              {data?.email ?? ''}
            </Typography>
          </Box>
        </Stack>
        <Divider sx={{ mx: 2, my: 1 }} />
        {main.map((item) => (
          <MuiMenuItem key={item.label} onClick={() => handleItemClick(item)}>
            {item.label}
          </MuiMenuItem>
        ))}
        <Divider sx={{ mx: 2, my: 1 }} />
        {rh.map((item) => (
          <MuiMenuItem key={item.label} onClick={() => handleItemClick(item)}>
            {item.label}
          </MuiMenuItem>
        ))}
        {rh.length > 0 && <Divider sx={{ mx: 2, my: 1 }} />}
        <MuiMenuItem key="logout" onClick={handleLogout}>
          Sair
        </MuiMenuItem>
      </Menu>
    </div>
  );
};
